import { useLocation } from "react-router";
import copy from "copy-to-clipboard";
import { Copy } from "lucide-react";
import { Layout } from "../UI/Layout/Layout.tsx";
import { NavBar } from "../UI/Layout/NavBar.tsx";
import { MainContainer } from "../UI/Layout/MainContainer.tsx";
import { Footer } from "../UI/Layout/Footer.tsx";
import { SuccessMessage } from "../UI/Success/SuccessMessage.tsx";
import { useMessageController } from "../UI/useMessageController.ts";
import { bonificoBankAccount } from "./bonificoBankAccount.ts";
import {
  bonificoTransferReasonPrefix,
  BonificoTransferReasonCustomer,
  GENERIC_TRANSFER_REASON_PREFIX,
} from "./bonificoTransferReason.ts";

export const BonificoBankDetailsScreenPath = "/bonifico/dati-bancari";

export type BonificoBankDetailsState = BonificoTransferReasonCustomer;

function BankDetailRow({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: (value: string) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-3 border-b border-gray-100">
      <div className="min-w-0">
        <p className="text-sm text-gray-500">{label}</p>
        <p className="font-semibold text-gray-900 break-all">{value}</p>
      </div>
      <button
        type="button"
        onClick={() => onCopy(value)}
        className="text-gray-400 hover:text-blue-600 shrink-0 p-1"
        aria-label={`Copia ${label}`}
      >
        <Copy className="w-4 h-4" />
      </button>
    </div>
  );
}

function TransferReasonRow({
  prefilledReason,
  onCopy,
}: {
  prefilledReason?: string;
  onCopy: (value: string) => void;
}) {
  return (
    <div className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-gray-500">Causale</p>
          <p className="font-semibold text-gray-900 break-words">
            {prefilledReason ?? GENERIC_TRANSFER_REASON_PREFIX}
            <span className="italic text-blue-700">[corso acquistato]</span>
          </p>
        </div>
        {prefilledReason && (
          <button
            type="button"
            onClick={() => onCopy(prefilledReason)}
            className="text-gray-400 hover:text-blue-600 shrink-0 p-1"
            aria-label="Copia Causale"
          >
            <Copy className="w-4 h-4" />
          </button>
        )}
      </div>
      <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800">
        Completa la causale aggiungendo alla fine il nome del corso che hai
        acquistato.
      </p>
    </div>
  );
}

export function BonificoBankDetailsScreen() {
  const location = useLocation();
  const state = location.state as BonificoBankDetailsState | null;
  const { showMessage, message, open } = useMessageController();

  const prefilledTransferReason = state
    ? bonificoTransferReasonPrefix(state)
    : undefined;

  const onCopy = (value: string) => {
    copy(value);
    showMessage({ message: "Copiato negli appunti!", timeoutMs: 3000 });
  };

  return (
    <Layout>
      <NavBar />
      <MainContainer>
        <SuccessMessage open={open} message={message} />
        <div className="max-w-2xl mx-auto p-6 bg-white w-full">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Dati per il bonifico
          </h1>
          <p className="text-gray-600 mb-6">
            Grazie, abbiamo ricevuto i tuoi dati. Per completare l'acquisto
            effettua un bonifico con le coordinate qui sotto, indicando la
            causale riportata completa del corso che hai acquistato.
          </p>

          <div className="bg-gray-50 rounded-lg px-4">
            <BankDetailRow
              label="Intestatario"
              value={bonificoBankAccount.accountHolder}
              onCopy={onCopy}
            />
            <BankDetailRow
              label="IBAN"
              value={bonificoBankAccount.iban}
              onCopy={onCopy}
            />
            {bonificoBankAccount.bicSwift && (
              <BankDetailRow
                label="BIC/SWIFT"
                value={bonificoBankAccount.bicSwift}
                onCopy={onCopy}
              />
            )}
            {bonificoBankAccount.bankName && (
              <BankDetailRow
                label="Banca"
                value={bonificoBankAccount.bankName}
                onCopy={onCopy}
              />
            )}
            <TransferReasonRow
              prefilledReason={prefilledTransferReason}
              onCopy={onCopy}
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <p className="text-sm text-blue-800">
              Una volta effettuato il bonifico, invia la contabile al tuo
              consulente di riferimento. Attiveremo il tuo corso appena la
              riceveremo.
            </p>
          </div>
        </div>
      </MainContainer>
      <Footer />
    </Layout>
  );
}
